import {HttpEvent, HttpEventType} from '@angular/common/http';
import {concatMap, map, OperatorFunction, pipe, scan} from 'rxjs';
import {filterNulls} from './rxjs';

export type SseFrame = {
    event: string;
    data: string;
};

type FrameWindow = {
    seen: number;
    fresh: string[];
};

const noFrames: FrameWindow = {seen: 0, fresh: []};

/** Split a raw buffer into complete frames; the trailing partial stays in `rest`. */
export function splitSseBuffer(buffer: string): {frames: string[]; rest: string} {
    const parts = buffer.split('\n\n');
    return {rest: parts.pop() ?? '', frames: parts};
}

/** Parse one frame's `event:` / `data:` lines. Multiple data lines join with newlines. */
export function parseSseFrame(frame: string): SseFrame {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) {
            event = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
            dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
        }
    }
    return {event, data: dataLines.join('\n')};
}

const streamedText = (event: HttpEvent<string>): string | null => {
    switch (event.type) {
        case HttpEventType.DownloadProgress:
            return event.partialText ?? null;
        case HttpEventType.Response:
            return event.body;
        default:
            return null;
    }
};

const newFrames = ({seen}: FrameWindow, text: string): FrameWindow => {
    const {frames} = splitSseBuffer(text);
    return {seen: frames.length, fresh: frames.slice(seen)};
};

export const sseFrames = (): OperatorFunction<HttpEvent<string>, SseFrame> =>
    pipe(
        map(streamedText),
        filterNulls(),
        scan(newFrames, noFrames),
        concatMap(({fresh}) => fresh),
        map(parseSseFrame),
    );
